import React, { useState } from 'react';
import { Link, Download, AlertCircle, CheckCircle } from 'lucide-react';
import { importAPI } from '../api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

export const ImportProductDialog = ({ isOpen, onClose, onImportSuccess }) => {
  const [singleURL, setSingleURL] = useState('');
  const [bulkURLs, setBulkURLs] = useState('');
  const [importing, setImporting] = useState(false);
  const [importedData, setImportedData] = useState(null);

  const handleSingleImport = async () => {
    if (!singleURL.trim()) {
      toast.error('Please enter a product URL');
      return;
    }

    try {
      setImporting(true);
      const response = await importAPI.importFromURL(singleURL);
      
      if (response.data.success) {
        setImportedData(response.data.data);
        toast.success('Product data imported! Review and save below.');
      } else {
        toast.error('Failed to import product data');
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(
        error.response?.data?.detail || 
        'Failed to import product. Please check the URL and try again.'
      );
    } finally {
      setImporting(false);
    }
  };

  const handleBulkImport = async () => {
    const urls = bulkURLs
      .split('\n')
      .map((url) => url.trim())
      .filter((url) => url.length > 0);

    if (urls.length === 0) {
      toast.error('Please enter at least one URL');
      return;
    }

    if (urls.length > 20) {
      toast.error('Maximum 20 URLs allowed at once');
      return;
    }

    try {
      setImporting(true);
      const response = await importAPI.importBulk(urls);
      
      const { successful, failed, results } = response.data;
      
      toast.success(
        `Import complete! ${successful} successful, ${failed} failed`
      );

      // Pass successful imports to parent
      const successfulProducts = results
        .filter((r) => r.success)
        .map((r) => r.data);
      
      if (successfulProducts.length > 0) {
        onImportSuccess(successfulProducts);
      }

      // Reset form
      setBulkURLs('');
      
    } catch (error) {
      console.error('Bulk import error:', error);
      toast.error('Failed to import products');
    } finally {
      setImporting(false);
    }
  };

  const handleSaveImported = () => {
    if (importedData) {
      onImportSuccess([importedData]);
      setSingleURL('');
      setImportedData(null);
    }
  };

  const handleDialogClose = () => {
    setSingleURL('');
    setBulkURLs('');
    setImportedData(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5" />
            Import Products from Amazon or Other Sites
          </DialogTitle>
          <DialogDescription>
            Paste product URLs from Amazon, Walmart, eBay, or other e-commerce sites to quickly import product details.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="single" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="single">Single Import</TabsTrigger>
            <TabsTrigger value="bulk">Bulk Import</TabsTrigger>
          </TabsList>

          {/* Single Import Tab */}
          <TabsContent value="single" className="space-y-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="url">Product URL</Label>
                <div className="flex gap-2">
                  <Input
                    id="url"
                    type="url"
                    value={singleURL}
                    onChange={(e) => setSingleURL(e.target.value)}
                    placeholder="https://www.amazon.com/product/..."
                    disabled={importing}
                  />
                  <Button
                    onClick={handleSingleImport}
                    disabled={importing || !singleURL.trim()}
                    className="bg-[#61525a] hover:bg-[#4a3f45]"
                  >
                    {importing ? 'Importing...' : 'Import'}
                  </Button>
                </div>
                <p className="text-sm text-[#736c64]">
                  Supported: Amazon, Walmart, eBay, and most e-commerce sites
                </p>
              </div>

              {/* Imported Data Preview */}
              {importedData && (
                <Card className="border-2 border-green-200 bg-green-50">
                  <CardContent className="p-4 space-y-4">
                    <div className="flex items-center gap-2 text-green-700 font-semibold">
                      <CheckCircle className="w-5 h-5" />
                      Product Data Imported Successfully!
                    </div>

                    <div className="grid gap-4">
                      {importedData.image && (
                        <div>
                          <Label className="text-sm text-[#736c64]">Image</Label>
                          <img
                            src={importedData.image}
                            alt="Product"
                            className="mt-2 w-32 h-32 object-cover rounded border"
                          />
                        </div>
                      )}

                      <div>
                        <Label className="text-sm text-[#736c64]">Name</Label>
                        <p className="mt-1 font-medium">{importedData.name}</p>
                      </div>

                      <div>
                        <Label className="text-sm text-[#736c64]">Price</Label>
                        <p className="mt-1 text-lg font-bold">
                          ${importedData.price?.toFixed(2) || '0.00'}
                        </p>
                      </div>

                      <div>
                        <Label className="text-sm text-[#736c64]">Description</Label>
                        <p className="mt-1 text-sm line-clamp-3">
                          {importedData.description}
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-4 border-t">
                      <Button
                        onClick={handleSaveImported}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        Use This Data
                      </Button>
                      <Button
                        onClick={() => setImportedData(null)}
                        variant="outline"
                        className="flex-1"
                      >
                        Try Different URL
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Help Section */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="space-y-2 text-sm">
                      <p className="font-semibold text-blue-900">Tips for best results:</p>
                      <ul className="space-y-1 text-blue-800 list-disc list-inside">
                        <li>Use direct product page URLs (not search results)</li>
                        <li>Amazon links work best with full URLs</li>
                        <li>Review imported data before saving</li>
                        <li>You can edit all fields after import</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Bulk Import Tab */}
          <TabsContent value="bulk" className="space-y-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bulk-urls">Product URLs (one per line)</Label>
                <Textarea
                  id="bulk-urls"
                  value={bulkURLs}
                  onChange={(e) => setBulkURLs(e.target.value)}
                  placeholder="https://www.amazon.com/product1/...&#10;https://www.amazon.com/product2/...&#10;https://www.walmart.com/product3/..."
                  rows={10}
                  disabled={importing}
                  className="font-mono text-sm"
                />
                <p className="text-sm text-[#736c64]">
                  Maximum 20 URLs at once. Products will be created automatically.
                </p>
              </div>

              <Button
                onClick={handleBulkImport}
                disabled={importing || !bulkURLs.trim()}
                className="w-full bg-[#61525a] hover:bg-[#4a3f45]"
                size="lg"
              >
                {importing ? 'Importing Products...' : 'Import All Products'}
              </Button>

              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="space-y-2 text-sm">
                      <p className="font-semibold text-amber-900">Bulk Import Notes:</p>
                      <ul className="space-y-1 text-amber-800 list-disc list-inside">
                        <li>All successfully imported products will be auto-saved</li>
                        <li>Failed imports will be reported but won't stop the process</li>
                        <li>You'll need to assign categories after import</li>
                        <li>Large imports may take a few minutes</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
